import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import { ShieldCheck, CircleCheck, Sparkles } from 'lucide-react';
import { joinPool } from '../services/api';
import { track, EVENTS } from '../services/analytics';

/* ----------------------------------------------------------------------------
 * Join the talent pool — public candidate self-signup. Neutral base + single
 * blue accent, editorial serif heading, hairline-bordered card. Matches the
 * Register.js design system. Candidates opt in to being discoverable to hiring
 * teams; on success we show their computed intent score.
 * -------------------------------------------------------------------------- */

const BLUE = '#2563EB';

const SENIORITY = ['junior', 'mid', 'senior', 'staff', 'lead'];
const REMOTE = ['remote', 'hybrid', 'onsite', 'flexible'];
const AVAILABILITY = [
  { value: 'open_now', label: 'Open now — actively looking' },
  { value: 'open_casual', label: 'Casually open' },
  { value: 'not_looking', label: 'Not looking right now' },
];

const TIMELINE = [
  { value: 'asap', label: 'ASAP' },
  { value: '1-3mo', label: 'In 1–3 months' },
  { value: '3-6mo', label: 'In 3–6 months' },
  { value: 'exploring', label: 'Just exploring' },
];

const reveal = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
};

function Join() {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    desired_roles: '',
    skills: '',
    target_seniority: 'mid',
    location: '',
    remote_preference: 'flexible',
    availability: 'open_now',
    timeline: 'asap',
    must_haves: '',
    deal_breakers: '',
    consent: true,
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...formData,
        // Comma-separated string -> array; backend also tolerates either form.
        skills: formData.skills
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
      };
      const res = await joinPool(payload);
      setResult(res.data);
      track(EVENTS.CANDIDATE_JOIN, { intent: res.data?.intent_score });
      toast.success("You're in the pool!");
    } catch (error) {
      toast.error(error.response?.data?.error || 'Something went wrong — please try again');
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    'appearance-none block w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-3.5 py-3 text-sm text-gray-900 dark:text-white placeholder-gray-400 shadow-sm transition-colors focus:border-[#2563EB] focus:outline-none focus:ring-1 focus:ring-[#2563EB]';

  const labelClass = 'block text-sm font-medium text-gray-700 dark:text-gray-300';

  const PERKS = [
    'Get found by motivated hiring teams',
    'No spam — you control your visibility',
    'Opt out anytime',
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC] dark:bg-gray-950 text-gray-900 dark:text-gray-100 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="sm:mx-auto sm:w-full sm:max-w-lg"
      >
        <div className="mb-1 inline-flex w-full items-center justify-center gap-2 text-xs font-medium uppercase tracking-[0.05em] text-gray-500">
          <span className="h-1.5 w-1.5 rounded-full" style={{ background: BLUE }} />
          Join the talent pool
        </div>
      </motion.div>

      <div className="mt-6 sm:mx-auto sm:w-full sm:max-w-lg">
        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-6 py-8 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_24px_48px_-24px_rgba(15,23,42,0.18)] sm:px-10"
        >
          <Link to="/" className="flex justify-center">
            <img className="h-20 w-auto" src="/AutoHire.png" alt="AutoHire" />
          </Link>

          {result ? (
            <div className="mt-6 text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-blue-50 ring-1 ring-blue-600/20">
                <Sparkles size={28} style={{ color: BLUE }} />
              </div>
              <h2 className="mt-5 font-serif text-3xl font-semibold tracking-tight text-gray-900 dark:text-white">
                You're in the pool
              </h2>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                Hiring teams searching for talent like you can now find your profile.
              </p>
              <div className="mt-6 inline-flex flex-col items-center rounded-2xl border border-gray-200 dark:border-gray-800 px-8 py-5">
                <span
                  className="text-4xl font-bold tabular-nums"
                  style={{ color: BLUE }}
                >
                  {result.intent_score}
                </span>
                <span className="mt-1 text-[11px] uppercase tracking-wide text-gray-500">
                  Your intent score
                </span>
              </div>
              <p className="mt-6 text-sm text-gray-500 dark:text-gray-400">
                We'll never spam you. You control your visibility.
              </p>
              {result.manage_token && (
                <p className="mt-4 text-sm">
                  <Link
                    to={`/profile/manage?token=${result.manage_token}`}
                    className="font-semibold underline-offset-2 hover:underline"
                    style={{ color: BLUE }}
                  >
                    Manage your profile / visibility
                  </Link>
                </p>
              )}
              <div className="mt-6 flex justify-center gap-3">
                <button
                  type="button"
                  onClick={() => setResult(null)}
                  className="rounded-lg border border-gray-300 dark:border-gray-700 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 transition hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  Update my details
                </button>
                <Link
                  to="/"
                  className="rounded-lg px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
                  style={{ background: BLUE }}
                >
                  Back home
                </Link>
              </div>
            </div>
          ) : (
            <>
              <h2 className="mt-5 text-center font-serif text-3xl font-semibold tracking-tight text-gray-900 dark:text-white">
                Join the talent pool
              </h2>
              <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
                Be discoverable to hiring teams looking for exactly what you do.
              </p>

              <form className="mt-8 space-y-5" onSubmit={handleSubmit}>
                <div>
                  <label htmlFor="full_name" className={labelClass}>Full name</label>
                  <div className="mt-1.5">
                    <input
                      id="full_name"
                      name="full_name"
                      type="text"
                      required
                      value={formData.full_name}
                      onChange={handleChange}
                      className={inputClass}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className={labelClass}>Email address</label>
                  <div className="mt-1.5">
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className={inputClass}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="desired_roles" className={labelClass}>Target roles</label>
                  <div className="mt-1.5">
                    <input
                      id="desired_roles"
                      name="desired_roles"
                      type="text"
                      placeholder="e.g. Senior Backend Engineer, Platform Engineer"
                      value={formData.desired_roles}
                      onChange={handleChange}
                      className={inputClass}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="skills" className={labelClass}>Skills</label>
                  <div className="mt-1.5">
                    <input
                      id="skills"
                      name="skills"
                      type="text"
                      placeholder="Comma-separated, e.g. Node.js, PostgreSQL, React"
                      value={formData.skills}
                      onChange={handleChange}
                      className={inputClass}
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-400">Separate with commas.</p>
                </div>

                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="target_seniority" className={labelClass}>Seniority</label>
                    <div className="mt-1.5">
                      <select
                        id="target_seniority"
                        name="target_seniority"
                        value={formData.target_seniority}
                        onChange={handleChange}
                        className={`${inputClass} capitalize`}
                      >
                        {SENIORITY.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="remote_preference" className={labelClass}>Remote preference</label>
                    <div className="mt-1.5">
                      <select
                        id="remote_preference"
                        name="remote_preference"
                        value={formData.remote_preference}
                        onChange={handleChange}
                        className={`${inputClass} capitalize`}
                      >
                        {REMOTE.map((r) => (
                          <option key={r} value={r}>{r}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div>
                  <label htmlFor="location" className={labelClass}>Location</label>
                  <div className="mt-1.5">
                    <input
                      id="location"
                      name="location"
                      type="text"
                      placeholder="e.g. San Francisco, CA or Remote"
                      value={formData.location}
                      onChange={handleChange}
                      className={inputClass}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="availability" className={labelClass}>Availability</label>
                  <div className="mt-1.5">
                    <select
                      id="availability"
                      name="availability"
                      value={formData.availability}
                      onChange={handleChange}
                      className={inputClass}
                    >
                      {AVAILABILITY.map((a) => (
                        <option key={a.value} value={a.value}>{a.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="timeline" className={labelClass}>Timeline</label>
                  <div className="mt-1.5">
                    <select
                      id="timeline"
                      name="timeline"
                      value={formData.timeline}
                      onChange={handleChange}
                      className={inputClass}
                    >
                      {TIMELINE.map((t) => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="must_haves" className={labelClass}>Must-haves <span className="font-normal text-gray-400">(optional)</span></label>
                  <div className="mt-1.5">
                    <input
                      id="must_haves"
                      name="must_haves"
                      type="text"
                      value={formData.must_haves}
                      onChange={handleChange}
                      placeholder="e.g. remote, equity, 4-day week"
                      className={inputClass}
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-400">Comma-separated.</p>
                </div>

                <div>
                  <label htmlFor="deal_breakers" className={labelClass}>Deal-breakers <span className="font-normal text-gray-400">(optional)</span></label>
                  <div className="mt-1.5">
                    <input
                      id="deal_breakers"
                      name="deal_breakers"
                      type="text"
                      value={formData.deal_breakers}
                      onChange={handleChange}
                      placeholder="e.g. on-call, no remote, crypto"
                      className={inputClass}
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-400">Comma-separated.</p>
                </div>

                <label className="flex items-start gap-3 rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950 p-3.5 text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    name="consent"
                    checked={formData.consent}
                    onChange={handleChange}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span>
                    I agree to be discoverable to hiring teams — you can opt out anytime.
                  </span>
                </label>

                <div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex w-full items-center justify-center rounded-lg px-4 py-3 text-sm font-semibold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#2563EB] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0"
                    style={{ background: BLUE }}
                    onMouseEnter={(e) => !loading && (e.currentTarget.style.background = '#1D4FD8')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = BLUE)}
                  >
                    {loading ? 'Joining…' : 'Join the pool'}
                  </button>
                </div>
              </form>

              <div className="mt-7 border-t border-gray-100 dark:border-gray-800 pt-5">
                <ul className="space-y-2">
                  {PERKS.map((perk) => (
                    <li key={perk} className="flex items-center gap-2 text-[13px] text-gray-600 dark:text-gray-400">
                      <CircleCheck size={15} style={{ color: BLUE }} />
                      {perk}
                    </li>
                  ))}
                </ul>
              </div>
            </>
          )}
        </motion.div>

        <motion.p
          {...reveal}
          transition={{ duration: 0.5, delay: 0.15 }}
          className="mt-6 flex items-center justify-center gap-1.5 text-xs text-gray-400"
        >
          <ShieldCheck size={13} style={{ color: BLUE }} />
          You control your visibility — opt out anytime
        </motion.p>
      </div>
    </div>
  );
}

export default Join;
